/**
 * Applies an imported backup payload to the Web client.
 *
 * The Web app only maps a subset of the Mac's preferences (editor font
 * size, sidebar width, onboarding flag) — LLM, cloud sync and MCP sections
 * have no Web equivalent and are reported back so the UI can gray them
 * out with an explanation instead of silently dropping them.
 */
import {
  type BackupPayload,
  type BackupSectionName,
} from './bundle.js';
import {
  clampEditorFontSize,
  EDITOR_FONT_SIZE_KEY,
} from '../editor/fontSize.js';
import {
  clampNoteListWidth,
  clampSidebarWidth,
  NOTE_LIST_WIDTH_KEY,
  SIDEBAR_WIDTH_KEY,
} from '../ui/paneLayout.js';
import { ONBOARDING_DONE_KEY } from '../onboarding.js';

export interface SkippedSection {
  section: string;
  reason: string;
}

export interface ImportReport {
  /** Human-readable lines describing what was applied, in apply order. */
  applied: string[];
  /** Sections present in the file that have no Web equivalent. */
  skipped: SkippedSection[];
  /** Preference fields present in the file that stay on the Mac. */
  skippedPreferences: string[];
}

const SECTION_REASONS: Record<Exclude<BackupSectionName, 'preferences'>, string> = {
  llm: 'LLM features live in the Mac app',
  cloudSync: 'This is the server — cloud sync is set up on your Macs',
  mcp: 'MCP servers live in the Mac app',
};

export type PreferencesStorage = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * Writes the applicable subset to `storage` (localStorage in the app, a fake
 * in tests). `viewportWidth` clamps the imported sidebar width exactly like
 * a drag would. Returns what happened, for the result UI.
 */
export function applyBackupToLocalStorage(
  payload: BackupPayload,
  viewportWidth: number,
  storage: PreferencesStorage = globalThis.localStorage,
): ImportReport {
  const report: ImportReport = { applied: [], skipped: [], skippedPreferences: [] };

  const prefs = payload.preferences;
  if (prefs) {
    if (prefs.editorFontSize !== undefined) {
      const size = clampEditorFontSize(prefs.editorFontSize);
      storage.setItem(EDITOR_FONT_SIZE_KEY, String(size));
      report.applied.push(`Editor font size — ${size} px`);
    }
    if (prefs.sidebarWidth !== undefined) {
      const width = Math.round(clampSidebarWidth(prefs.sidebarWidth, viewportWidth));
      storage.setItem(SIDEBAR_WIDTH_KEY, String(width));
      storage.setItem(
        NOTE_LIST_WIDTH_KEY,
        String(Math.round(clampNoteList(storage, viewportWidth, width))),
      );
      report.applied.push(`Sidebar width — ${width} px`);
    }
    if (prefs.onboardingCompleted === true) {
      storage.setItem(ONBOARDING_DONE_KEY, '1');
      report.applied.push('Setup wizard marked done');
    }

    if (prefs.language !== undefined) report.skippedPreferences.push('Language');
    if (prefs.contentMode !== undefined) report.skippedPreferences.push('Reading mode');
    if (
      prefs.dictationShortcutKey !== undefined ||
      prefs.dictationShortcutModifiers !== undefined
    ) {
      report.skippedPreferences.push('Dictation shortcut');
    }
  }

  for (const section of ['llm', 'cloudSync', 'mcp'] as const) {
    if (payload[section] != null) {
      report.skipped.push({
        section,
        reason: SECTION_REASONS[section],
      });
    }
  }

  return report;
}

/** Note-list width re-clamped around the imported sidebar width. */
function clampNoteList(storage: PreferencesStorage, viewportWidth: number, sidebarWidth: number): number {
  const raw = storage.getItem(NOTE_LIST_WIDTH_KEY);
  const current = raw !== null ? Number.parseFloat(raw) : Number.NaN;
  const base = Number.isFinite(current) ? current : 300;
  return clampNoteListWidth(base, viewportWidth, sidebarWidth);
}

/** Notifies the running app that preferences changed underneath it. */
export function notifyPreferencesChanged(): void {
  try {
    window.dispatchEvent(new CustomEvent('jnote:preferences-imported'));
  } catch {
    // Not a browser (tests) — nothing to notify.
  }
}
